import { Autocomplete, Checkbox, Chip, ListItem, ListItemButton, ListItemDecorator, Typography, ListSubheader, Box, ChipDelete } from '@mui/joy'
import { allTypes as rawAllTypes, subTypes as rawSubTypes } from 'utils/truckEquipmentTypes'
import { FormikProps } from 'formik'
import { useMemo, useState, useRef, useEffect } from 'react'
import { Equipment, EquipmentTypes } from '@numeo/types-core'
import { POPOVER_Z_INDEX } from 'components/ds2/z-index'

const allTypes: EquipmentOption[] = rawAllTypes.map((type) => ({ ...type, header: false, special: false, category: 'all' }))
const subTypes: EquipmentOption[] = rawSubTypes.map((type) => ({ ...type, header: false, special: false, category: 'only' }))

// Equipment Multi Select is component for selecting multi equipments based on types

export interface EquipmentMultiSelectProps<T extends { equipment: Equipment }> {
    disabled?: boolean
    formik: FormikProps<T>
    onChange?: (value: Equipment) => void
}

interface EquipmentOption {
    code: string
    name: string
    header?: boolean
    special?: boolean
    category?: 'all' | 'only'
}

export const EquipmentMultiSelect = <T extends { equipment: Equipment }>({ disabled = false, formik, onChange }: EquipmentMultiSelectProps<T>) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }

        updateWidth()
        window.addEventListener('resize', updateWidth)
        return () => window.removeEventListener('resize', updateWidth)
    }, [])
    const calculateLimitTags = useMemo(() => {
        if (containerWidth < 220) return 1
        if (containerWidth < 280) return 1
        if (containerWidth < 350) return 1
        if (containerWidth < 400) return 1
        return 5
    }, [containerWidth])
    const selectedValues = useMemo(() => formik.values.equipment?.values || [], [formik.values.equipment])

    const hasAllTypeSelected = useMemo(() => selectedValues.some((value) => allTypes.some((type) => type.code === value)), [selectedValues])

    const hasOnlyTypeSelected = useMemo(() => selectedValues.some((value) => subTypes.some((type) => type.code === value)), [selectedValues])

    // Check if any equipment is selected
    const hasAnyEquipmentSelected = useMemo(() => selectedValues.length > 0, [selectedValues])

    // Check if all options in each category are selected
    const allAllTypesSelected = useMemo(() => allTypes.every((type) => selectedValues.includes(type.code)), [selectedValues])

    const allSubTypesSelected = useMemo(() => subTypes.every((type) => selectedValues.includes(type.code)), [selectedValues])

    const handleChange = (
        event: React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | React.FocusEvent<Element, Element> | null,
        newValue: EquipmentOption[]
    ) => {
        // Check if the new selection would mix ALL and ONLY types
        const newHasAllType = newValue.some((value) => allTypes.some((type) => type.code === value.code))
        const newHasOnlyType = newValue.some((value) => subTypes.some((type) => type.code === value.code))

        // If mixing, keep only the new type category
        let finalValue = newValue
        if (newHasAllType && newHasOnlyType) {
            // Find which type was just added by comparing with current selection
            const addedValue = newValue.find((v) => !selectedValues.includes(v.code))

            if (addedValue) {
                // Determine which category the added value belongs to
                const isAddedValueFromAll = allTypes.some((type) => type.code === addedValue.code)

                // Keep only values from the same category as the added value
                finalValue = isAddedValueFromAll
                    ? newValue.filter((v) => allTypes.some((type) => type.code === v.code))
                    : newValue.filter((v) => subTypes.some((type) => type.code === v.code))
            }
        }

        // Check if "other" is selected
        if (newValue.some((item) => item.code === 'other')) {
            handleClearSelection()
            return
        }

        const equipment: Equipment = {
            type: newHasAllType ? EquipmentTypes.CLASSES : EquipmentTypes.TYPES,
            values: finalValue.map((item) => item.code),
        }

        formik.setFieldValue('equipment', equipment)
        onChange?.(equipment)
    }

    // Handle select/unselect all for a category
    const handleSelectAllCategory = (category: 'all' | 'only') => {
        const currentValues = [...selectedValues]
        const categoryTypes = category === 'all' ? allTypes : subTypes
        const otherCategoryTypes = category === 'all' ? subTypes : allTypes

        // Check if all items in this category are already selected
        const allSelected = categoryTypes.every((type) => currentValues.includes(type.code))

        let newValues: string[]

        if (allSelected) {
            // If all are selected, unselect all in this category
            newValues = currentValues.filter((value) => !categoryTypes.some((type) => type.code === value))
        } else {
            // If not all selected, select all in this category and remove any from other category
            newValues = [...currentValues.filter((value) => !otherCategoryTypes.some((type) => type.code === value)), ...categoryTypes.map((type) => type.code)]
        }

        const equipment: Equipment = {
            type: category === 'all' ? EquipmentTypes.CLASSES : EquipmentTypes.TYPES,
            values: newValues,
        }

        formik.setFieldValue('equipment', equipment)
        onChange?.(equipment)
    }

    // Handle clearing all selections
    const handleClearSelection = () => {
        const equipment: Equipment = {
            type: EquipmentTypes.TYPES,
            values: [],
        }

        formik.setFieldValue('equipment', equipment)
        onChange?.(equipment)
    }

    return (
        <Box ref={containerRef} sx={{ width: '100%' }}>
            <Autocomplete
                multiple
                placeholder="Select equipment type"
                disabled={disabled}
                value={selectedValues.map(
                    (code) => ([...allTypes, ...subTypes] as EquipmentOption[]).find((type) => type.code === code) || ({ code, name: code } as EquipmentOption)
                )}
                limitTags={calculateLimitTags}
                sx={{
                    '& .MuiAutocomplete-root': {
                        display: 'flex',
                        flexWrap: 'nowrap',
                        overflow: 'hidden',
                    },
                }}
                slotProps={{
                    // Joy portals the listbox to <body> at zIndex.popup (1000), which lands BEHIND
                    // the ds2 Modal (backdrop z-9998) hosting this select in TruckModal. 10000
                    // matches ds2's own popover layer, and dropdowns-above-everything is safe in
                    // every non-modal context too.
                    listbox: { sx: { zIndex: POPOVER_Z_INDEX } },
                    input: {
                        'aria-label': 'equipment-type-select',
                        name: 'equipment',
                        'data-testid': 'equipment-select',
                        sx: {
                            '--Input-minHeight': '44px',
                            py: 0.75,
                        },
                    },
                    limitTag: {
                        sx: {
                            position: 'absolute',
                            right: 60,
                            mb: 0.5,
                        },
                    },
                }}
                onChange={(_, newValue) => {
                    handleChange(null, newValue)
                }}
                options={[
                    // Always include the All header if not mixing categories
                    ...(hasOnlyTypeSelected ? [] : [{ code: 'all-header', name: 'All', header: true, category: 'all' } as EquipmentOption]),
                    // Always include All types if not mixing categories
                    ...(hasOnlyTypeSelected ? [] : allTypes),
                    // Always include the Only header if not mixing categories
                    ...(hasAllTypeSelected ? [] : [{ code: 'only-header', name: 'Only', header: true, category: 'only' } as EquipmentOption]),
                    // Always include Only types if not mixing categories
                    ...(hasAllTypeSelected ? [] : subTypes),
                    // Include Other option if any equipment is selected
                    ...(hasAnyEquipmentSelected ? [{ code: 'other', name: 'Other', special: true } as EquipmentOption] : []),
                ]}
                filterOptions={(options, { inputValue }) => {
                    // Custom filter to maintain category separation
                    if (!inputValue) return options

                    const filtered: EquipmentOption[] = []
                    const allHeader = options.find((opt) => opt.code === 'all-header')
                    const onlyHeader = options.find((opt) => opt.code === 'only-header')
                    const otherOption = options.find((opt) => opt.code === 'other')

                    // Filter All types
                    const filteredAllTypes = options
                        .filter((opt) => opt.category === 'all' && !opt.header)
                        .filter((opt) => opt.name.toLowerCase().includes(inputValue.toLowerCase()))

                    // Filter Only types
                    const filteredOnlyTypes = options
                        .filter((opt) => opt.category === 'only' && !opt.header)
                        .filter((opt) => opt.name.toLowerCase().includes(inputValue.toLowerCase()))

                    // Add All header and filtered All types if any match
                    if (filteredAllTypes.length > 0 && allHeader) {
                        filtered.push(allHeader)
                        filtered.push(...filteredAllTypes)
                    }

                    // Add Only header and filtered Only types if any match
                    if (filteredOnlyTypes.length > 0 && onlyHeader) {
                        filtered.push(onlyHeader)
                        filtered.push(...filteredOnlyTypes)
                    }

                    // Add Other option if present
                    if (otherOption) {
                        filtered.push(otherOption)
                    }

                    return filtered
                }}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) =>
                    Boolean(option.code === value.code || (option.header && value.header) || (option.special && value.special))
                }
                renderTags={(selected, getTagProps) =>
                    selected.map((option: EquipmentOption, index: number) => {
                        const chipProps = getTagProps({ index })
                        const handleChipDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
                            const newSelected = selected.filter((_, i) => i !== index)
                            handleChange(event, newSelected)
                        }
                        const truncatedName = option.name.length > 15 ? `${option.name.substring(0, 15)}...` : option.name
                        return (
                            <Chip variant="soft" color="primary" endDecorator={<ChipDelete onDelete={handleChipDelete} />} {...chipProps} key={option.code}>
                                {truncatedName}
                            </Chip>
                        )
                    })
                }
                renderOption={(props, option: EquipmentOption, { selected }) => {
                    if (option.header) {
                        return (
                            <ListSubheader
                                sx={{
                                    textTransform: 'uppercase',
                                    fontSize: 'sm',
                                    p: 1,
                                    bgcolor: 'primary.softBg',
                                    top: 0,
                                    zIndex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    borderTop: '1px solid',
                                    borderBottom: '1px solid',
                                    borderColor: 'primary.softActiveBg',
                                    mt: option.category === 'only' ? 0 : 1,
                                }}
                            >
                                {option.name}
                                <Checkbox
                                    checked={option.category === 'all' ? allAllTypesSelected : allSubTypesSelected}
                                    onChange={() => handleSelectAllCategory(option.category!)}
                                    disabled={disabled}
                                    size="sm"
                                    sx={{ ml: 1, boxShadow: 'xs' }}
                                />
                            </ListSubheader>
                        )
                    } else if (option.special) {
                        return (
                            <ListItem
                                {...props}
                                key={option.code}
                                sx={{
                                    fontSize: {
                                        xs: '0.9rem',
                                        sm: '0.94rem',
                                        md: '0.97rem',
                                        lg: '1rem',
                                    },
                                    color: 'primary.600',
                                    '&:hover': {
                                        backgroundColor: 'primary.100',
                                        color: 'primary.700',
                                    },
                                    fontWeight: 'md',
                                    mt: 1,
                                    pt: 0.5,
                                    pb: 0.5,
                                    borderTop: '1px dashed',
                                    borderColor: 'divider',
                                }}
                            >
                                {option.name}
                            </ListItem>
                        )
                    } else {
                        return (
                            <ListItem {...props} key={option.code}>
                                <ListItemButton>
                                    <ListItemDecorator>
                                        <Checkbox checked={selected} />
                                    </ListItemDecorator>
                                    <Typography>{option.name}</Typography>
                                </ListItemButton>
                            </ListItem>
                        )
                    }
                }}
            ></Autocomplete>
        </Box>
    )
}
